import React from 'react';
import './SpellDetails.css';
import MediaImg from '../utils/MediaImg';
import GameText from '../utils/GameText';
import { damageLinesColorStyle, getSpellLineIcon } from '../../core/weapon';
import {
    dedupeIdenticalAlternatives,
    filterSpellLinesForDisplay,
    fmtCondLabel,
    formatSpellLineConditionText,
    sortRowsByConditionDisplayOrder,
} from '../../core/spellUtils';

const TXT_EN = { fr: 'fr', en: 'en', de: 'de', es: 'es', pt: 'pt' };

const PERCENT_MAX_HP_HEAL_EFFECT_IDS = new Set([1109]);

const fmtRange = (v) => {
    if (v?.min == null && v?.max == null) return '-';
    if (v?.min != null && v?.max != null) return `${v.min} - ${v.max}`;
    if (v?.min != null) return `${v.min}`;
    return `${v.max}`;
};

const isPercentMaxHpHealEffect = (line) =>
    line?.kind === 'heal' && PERCENT_MAX_HP_HEAL_EFFECT_IDS.has(Number(line?.effectId));

const fmtPercentRange = (v) => {
    if (v?.min == null && v?.max == null) return '-';
    if (v?.min != null && v?.max != null) return v.min === v.max ? `${v.min}%` : `${v.min} - ${v.max}%`;
    if (v?.min != null) return `${v.min}%`;
    return `${v.max}%`;
};

const LOS_LBL = {
    true: { fr: 'Ligne de vue', en: 'Line of sight', de: 'Sichtlinie', es: 'Línea de visión', pt: 'Linha de visão' },
    false: { fr: 'Sans ligne de vue', en: 'No line of sight', de: 'Ohne Sichtlinie', es: 'Sin línea de visión', pt: 'Sem linha de visão' },
};

const META_LBL = {
    ap: { fr: 'PA', en: 'AP', de: 'AP', es: 'PA', pt: 'PA' },
    crit: { fr: 'Crit', en: 'Crit', de: 'Krit', es: 'Crít', pt: 'Crít' },
    range: { fr: 'Portée', en: 'Range', de: 'Reichweite', es: 'Alcance', pt: 'Alcance' },
    los: { fr: 'LdV', en: 'LOS', de: 'SL', es: 'LdV', pt: 'LdV' },
};

const HEADERS_LBL = {
    type: { fr: 'Dégâts', en: 'Damage', de: 'Schaden', es: 'Daños', pt: 'Danos' },
    element: { fr: 'Élément', en: 'Element', de: 'Element', es: 'Elemento', pt: 'Elemento' },
    normal: { fr: 'Normal', en: 'Normal', de: 'Normal', es: 'Normal', pt: 'Normal' },
    critical: { fr: 'Critique', en: 'Critical', de: 'Kritisch', es: 'Crítico', pt: 'Crítico' },
};

const fmtRangeDisplay = (min, max) => {
    if (min == null && max == null) return '-';
    if (min != null && max != null) return min === max ? `${min}` : `${min}–${max}`;
    return min != null ? `${min}` : `${max}`;
};

const fmtChancePct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `${n.toFixed(2)}%`;
};

const getCondText = (line, blockLabel, lang, healLabel, chancePct = null) => {
    const chanceTxt = fmtChancePct(chancePct);
    return formatSpellLineConditionText({
        line,
        blockLabel,
        lang,
        healLabel,
        fallback: 'direct',
        suffix: chanceTxt,
    });
};

const normalizeDescriptionText = (text) => {
    if (!text) return '';
    return String(text)
        .replace(/\r\n?/g, '\n')
        .trim();
};

const renderDescription = (text) => {
    const normalized = normalizeDescriptionText(text);
    if (!normalized) return null;
    return <p className="spell-details-description"><GameText text={normalized} /></p>;
};

const PORTAL_CASTS = ['direct', 'through_portal'];

/**
 * A portal spell as two casts, rather than a hit plus a footnote.
 *
 * Its ungated lines land either way, so listing them once left them under the fallback
 * "Direct" label — which, sitting above a real "Sans portail" row, claimed they were
 * gated on the direct cast. Affront's air damage is unconditional; only its push is not.
 * Each cast now lists everything it lands, and groupConditionalRows merges the repeated
 * label into one cell.
 *
 * Null unless the spell splits on the gate itself. A `through_portal` block with no
 * `direct` twin is a portal *bonus* (Extinction's end-of-turn hit, Affliction's), and
 * its own label already says when it happens.
 */
const flattenPortalCastLines = (spell, lang) => {
    const blocks = spell.conditionalBlocks || [];
    if (!blocks.some((block) => block.condKey === 'direct')) return null;
    const out = [];
    PORTAL_CASTS.forEach((gate) => {
        const block = blocks.find((b) => b.condKey === gate);
        const label = block?.label || fmtCondLabel(gate, null, lang);
        [...(spell.baseLines || []), ...(block?.lines || [])].forEach((line, idx) => {
            out.push({ line, blockLabel: label, key: `${spell.id}-cast-${gate}-${line.el}-${idx}` });
        });
    });
    return out;
};

const flattenLines = (spell, lang) => {
    const out = [];
    const castLines = flattenPortalCastLines(spell, lang);
    if (castLines) {
        out.push(...castLines);
    } else {
        spell.baseLines.forEach((line, idx) => {
            out.push({ line, blockLabel: null, key: `${spell.id}-base-${line.el}-${idx}` });
        });
    }
    spell.conditionalBlocks.forEach((block, bi) => {
        if (castLines && PORTAL_CASTS.includes(block.condKey)) return;
        block.lines.forEach((line, idx) => {
            out.push({ line, blockLabel: block.label, key: `${spell.id}-cond-${bi}-${line.el}-${idx}` });
        });
    });
    (spell.bonusLines || []).forEach((line, idx) => {
        out.push({ line, blockLabel: null, key: `${spell.id}-bdb-${idx}` });
    });
    (spell.summonChildRows || []).forEach((cr) => {
        (cr.baseLines || []).forEach((line, idx) => {
            out.push({ line, blockLabel: cr.name, key: `${spell.id}-sc-${cr.id}-b-${idx}` });
        });
        (cr.conditionalBlocks || []).forEach((block, bi) => {
            block.lines.forEach((line, idx) => {
                out.push({
                    line,
                    blockLabel: [cr.name, block.label].filter(Boolean).join(' · '),
                    key: `${spell.id}-sc-${cr.id}-c-${bi}-${idx}`,
                });
            });
        });
        (cr.bonusLines || []).forEach((line, idx) => {
            out.push({ line, blockLabel: cr.name, key: `${spell.id}-sc-${cr.id}-bb-${idx}` });
        });
    });
    // The table listed every line Dofus writes, so one hit described once per target
    // category arrived as several identical rows under invented labels — Resquille read
    // as "Direct" plus two "Conditionnel" rows of the same 33-37.
    const kept = new Set(dedupeIdenticalAlternatives(out.map(({ line }) => line)));
    return out.filter(({ line }) => kept.has(line));
};

const conditionalGroupKey = (row) => {
    // Element-variant lines are mutually exclusive alternatives, not one
    // multi-element hit. Never merge their condition cell via rowSpan so each
    // element renders as its own labelled row.
    if (row?.line?.elVariantXorKey) return null;
    const condText = String(row?.condText || '').trim().toLowerCase();
    if (!condText) return null;
    const blockLabel = String(row?.blockLabel || '').trim().toLowerCase();
    return `${blockLabel}|${condText}`;
};

const groupConditionalRows = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) return rows || [];
    const out = rows.map((row) => ({
        ...row,
        condRowSpan: 1,
        hideCondCell: false,
        tightTop: false,
        tightBottom: false,
    }));
    let i = 0;
    while (i < out.length) {
        const key = conditionalGroupKey(out[i]);
        if (!key) {
            i += 1;
            continue;
        }
        let j = i + 1;
        while (j < out.length && conditionalGroupKey(out[j]) === key) {
            j += 1;
        }
        const span = j - i;
        if (span > 1) {
            out[i].condRowSpan = span;
            for (let k = i + 1; k < j; k += 1) {
                out[k].hideCondCell = true;
            }
            for (let k = i; k < j; k += 1) {
                if (k > i) out[k].tightTop = true;
                if (k < j - 1) out[k].tightBottom = true;
            }
        }
        i = j;
    }
    return out;
};

/**
 * Title bar for the touch overlay: the spell's own icon plus what kind of thing
 * it is. The name stays on the panel below, the way the item overlay does it.
 */
export const SpellDetailsOverlayHeader = ({ icon, title }) => (
    <div className="spell-details-overlay-header row">
        {icon ? (
            <MediaImg className="spell-details-overlay-icon" src={icon} alt="" />
        ) : (
            <div className="spell-details-overlay-icon spell-details-overlay-icon--fallback" aria-hidden="true" />
        )}
        <h6 className="spell-details-overlay-title">{title}</h6>
    </div>
);

const SpellDetails = ({ spell: rawSpell, labels, lang = 'fr', embedded = false }) => {
    const spell = filterSpellLinesForDisplay(rawSpell);
    const lines = flattenLines(spell, lang);
    const chanceGroupTotals = (() => {
        const totals = new Map();
        for (const entry of lines) {
            const rnd = Number(entry?.line?.randomChance);
            if (!Number.isFinite(rnd) || rnd <= 0) continue;
            const key = [
                entry?.line?.kind || '',
                entry?.line?.targetMask || '',
                entry?.blockLabel || '',
                entry?.line?.condition || '',
                entry?.line?.targetLabel || '',
            ].join('|');
            totals.set(key, (totals.get(key) || 0) + rnd);
        }
        return totals;
    })();
    const elIcon = (line) => getSpellLineIcon(line);
    const l = TXT_EN[lang] || TXT_EN.en;
    const losText = spell.castLos === true || spell.castLos === false
        ? LOS_LBL[String(spell.castLos)][l]
        : '-';
    const critText = spell?.canCrit === false
        ? '-'
        : (spell.critPct != null ? `${spell.critPct}%` : '-');
    const rows = groupConditionalRows(sortRowsByConditionDisplayOrder(lines
        .map(({ key, line, blockLabel }, idx) => {
            const chanceKey = [
                line?.kind || '',
                line?.targetMask || '',
                blockLabel || '',
                line?.condition || '',
                line?.targetLabel || '',
            ].join('|');
            const rawChance = Number(line?.randomChance);
            const denom = chanceGroupTotals.get(chanceKey) || 0;
            const normalizedChance = Number.isFinite(rawChance) && rawChance > 0 && denom > 0
                ? (rawChance / denom) * 100
                : null;
            const condText = getCondText(line, blockLabel, lang, spell?.healLabel, normalizedChance);
            return { key, line, blockLabel, condText, idx };
        })));

    return (
        <div className={`spell-details${embedded ? ' spell-details--embedded' : ''}`}>
            <h5 className="spell-details-title">{spell.name}</h5>
            {renderDescription(spell.description)}
            <div className="spell-details-divider" role="presentation" />
            {lines.length > 0 && (
                <table className="spell-details-table">
                    <thead>
                        <tr>
                            <th className="spell-details-th spell-details-th-type">{HEADERS_LBL.type[l]}</th>
                            <th
                                className="spell-details-th spell-details-th-element"
                                scope="col"
                                aria-label={HEADERS_LBL.element[l]}
                            />
                            <th className="spell-details-th spell-details-th-normal">{HEADERS_LBL.normal[l]}</th>
                            <th className="spell-details-th spell-details-th-critical">{HEADERS_LBL.critical[l]}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ key, line, condText, condRowSpan, hideCondCell, tightTop, tightBottom }) => {
                            const baseNormal = line.baseRaw ?? line.base;
                            const baseCrit = line.critRaw ?? line.crit;
                            const totalNormal = line.base;
                            const totalCrit = line.crit;
                            const rawFmt = isPercentMaxHpHealEffect(line) ? fmtPercentRange : fmtRange;
                            const trClass = [
                                'spell-details-tr',
                                tightTop ? 'spell-details-tr--tight-top' : '',
                                tightBottom ? 'spell-details-tr--tight-bottom' : '',
                            ].filter(Boolean).join(' ');
                            return (
                                // The row publishes the colour of the element it
                                // lands in; only the computed values read it, so
                                // the dice above them stay subtle.
                                <tr key={key} className={trClass} style={damageLinesColorStyle([line])}>
                                    {!hideCondCell && (
                                        <td
                                            className={`spell-details-td spell-details-type${condRowSpan > 1 ? ' spell-details-type--grouped' : ''}`}
                                            rowSpan={condRowSpan}
                                        >
                                            {condText}
                                        </td>
                                    )}
                                    <td className="spell-details-td spell-details-element">
                                        {elIcon(line) ? (
                                            <img src={elIcon(line)} alt="" className="spell-details-el-icon" aria-hidden="true" />
                                        ) : (
                                            <span className="spell-details-el">
                                                {line.el === 'heal_total' ? spell.healLabel : (labels[line.el] || line.el)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="spell-details-td spell-details-normal">
                                        <div className="spell-details-dual">
                                            <span className="spell-details-val-base">{rawFmt(baseNormal)}</span>
                                            <span className="spell-details-val">{fmtRange(totalNormal)}</span>
                                        </div>
                                    </td>
                                    <td className="spell-details-td spell-details-crit">
                                        <div className="spell-details-dual">
                                            <span className="spell-details-val-base">{baseCrit != null ? rawFmt(baseCrit) : '-'}</span>
                                            <span className="spell-details-val">{totalCrit != null ? fmtRange(totalCrit) : '-'}</span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            <div className="spell-details-divider" role="presentation" />
            <div className="spell-details-footer">
                <span className="spell-details-meta">
                    {spell.apCost ?? '-'} <strong>{META_LBL.ap[l]}</strong>
                </span>
                <span className="spell-details-meta">
                    {critText} <strong>{META_LBL.crit[l]}</strong>
                </span>
                <span className="spell-details-meta">
                    {fmtRangeDisplay(spell.rangeMin, spell.rangeMax)} <strong>{META_LBL.range[l]}</strong>
                </span>
                <span className="spell-details-meta">{losText}</span>
            </div>
        </div>
    );
};

export default SpellDetails;
